/* eslint-disable */
import React, { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import { Helmet } from 'react-helmet-async';
import LoadingButton from '@mui/lab/LoadingButton';
import { useNavigate } from 'react-router-dom';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useForm } from 'react-hook-form';
import Box from '@mui/material/Box';
import toast, { Toaster } from 'react-hot-toast';
import Typography from '@mui/material/Typography';
import { useQueryVerifyLoginEmail, useMutationForgotPassword } from '../../hooks/react-query/useData';
import useFirebaseMessaging from '../../firebase';
import ResendDialog from 'src/components/dialog/ResendEmailDialogue';
import Logo from '../../components/logo';
import OtpVerificationScreen from '../../components/auth/OtpVerificationScreen';
import {
  useMutationSendOTP,
  useMutationVerifyOTP,
  useMutationResendOTP,
} from '../../hooks/react-query/twoFactor';

export default function SignIn() {
  const [loginCredentials, setLoginCredentials] = useState({});

  const [forgetPasswordCredentials, setForgetPasswordCredentials] = useState({});

  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showResendDialogue, setShowResendDialogue] = useState(false);
  const [show2FAScreen, setShow2FAScreen] = useState(false);
  const [pendingUserData, setPendingUserData] = useState(null);
  const [otpError, setOtpError] = useState(null);
  const navigate = useNavigate();
  const { requestNotificationPermission } = useFirebaseMessaging();

  const onSuccess = (success) => {
   
    if (success.data.message.includes('Login successfully.')) {
      if (success.data.data.signingUrl) {
        toast("You have not signed your contract. You will be redirecting to DocuSign")
        setTimeout(() => {
          window.open(success.data.data.signingUrl, '_blank');
        }, 6000)
        navigate('/login');
      } else {
        // Check if 2FA is enabled
        if (success.data.data.is2FAEnabled) {
          // Store user data temporarily and show 2FA screen
          setPendingUserData(success.data.data);
          setShow2FAScreen(true);
          // Send OTP
          sendOTP({ 
            email: success.data.data.email, 
            phone: success.data.data.phone 
          });
        } else {
          // Normal login without 2FA
          localStorage.setItem('user', JSON.stringify(success?.data?.data));
          if(success?.data?.data?.role === 'SITE-LEADER'){
            navigate('/dashboard');
          } else if(success?.data?.data?.role === 'PRINCIPAL'){
            navigate('/dashboard/principal/dashboard');
          } else {
            navigate('/dashboard/my-profile');
          }
        }
      }
    }
  };

  const onError = (error) => {

    if (error?.message === 'Network Error') {
      toast.error('Server Error');
      return;
    }
    if (error?.response?.data?.data?.isVerified === false) {
      setShowResendDialogue(true)
    }
    toast.error(error?.response?.data?.message, { duration: 5000 });
  };

  const {
    refetch: loginCurrentUser,
    data: loginData,
    isLoading: loginLoading,
  } = useQueryVerifyLoginEmail(loginCredentials, onSuccess, onError);

  const { mutate: forgotMyPassword, isLoading: isForgetPasswordsLoading } = useMutationForgotPassword(
    forgetPasswordCredentials,
    toast,
    setIsLogin
  );

  // 2FA mutations
  const { mutate: sendOTP, isLoading: isSendingOTP } = useMutationSendOTP(toast, (data) => {
    console.log('OTP sent successfully');
  });

  const { mutate: verifyOTP, isLoading: isVerifyingOTP } = useMutationVerifyOTP(toast, (data) => {
    // OTP verified, complete login
    if (data.data) {
      localStorage.setItem('user', JSON.stringify(pendingUserData));
      setOtpError(null);
      
      if(pendingUserData?.role === 'SITE-LEADER'){
        navigate('/dashboard');
      } else if(pendingUserData?.role === 'PRINCIPAL'){
        navigate('/dashboard/principal/dashboard');
      } else {
        navigate('/dashboard/my-profile');
      }
    }
  });

  const { mutate: resendOTP, isLoading: isResendingOTP } = useMutationResendOTP(toast, (data) => {
    setOtpError(null);
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm({});

  useEffect(() => {
    if (Object.keys(loginCredentials).length > 0) {
      loginCurrentUser();
    }
  }, [loginCredentials]);

  useEffect(() => {
    if (Object.keys(forgetPasswordCredentials).length > 0) {
      forgotMyPassword();
    }
  }, [forgetPasswordCredentials]);

  const onSubmit = (data) => {
    (async () => {
      const token = await requestNotificationPermission();

      if (token) {
        setLoginCredentials({ ...data, deviceToken: String(token) });
      } else {
        setLoginCredentials({ ...data });
      }
    })().catch((err) => {
      setLoginCredentials({ ...data });
    });
  };

  const forgotPasswordSubmit = async (data) => {
    setForgetPasswordCredentials({ email: data?.email });
  };

  const handleVerifyOTP = (otpCode) => {
    setOtpError(null);
    verifyOTP({
      email: pendingUserData?.email,
      otp: otpCode,
    });
  };

  const handleResendOTP = () => {
    resendOTP({ email: pendingUserData?.email });
  };

  const handleBackToLogin = () => {
    setShow2FAScreen(false);
    setPendingUserData(null);
    setOtpError(null);
  };

  // Show 2FA screen if enabled
  if (show2FAScreen && pendingUserData) {
    return (
      <>
        <Helmet>
          <title>Edifying Teachers | Two-Factor Authentication</title>
        </Helmet>
        <OtpVerificationScreen
          phoneNumber={pendingUserData.phone}
          onVerify={handleVerifyOTP}
          onResend={handleResendOTP}
          onBack={handleBackToLogin}
          isLoading={isVerifyingOTP}
          error={otpError}
        />
      </>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        bgcolor: 'background.default',
        justifyContent: 'center',
      }}
    >
      <Helmet>
        <title>Edifying Teachers | {isLogin ? 'Login' : 'Forgot Password'}</title>
      </Helmet>
      {/* <Toaster /> */}
      <Box
        sx={{
          backgroundImage: { xs: '', sm: 'url(/assets/illustrations/loginFormBg.svg)' },
          width: '644px',
          height: '648px',
          display: 'grid',
          my: 'auto',
          backgroundRepeat: 'no-repeat',
          backgroundSize: { xs: 'contain', sm: 'cover' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: { xs: '350px', sm: '408px' },
            p: '59px 43px 72.98px 38px',
            boxShadow: (theme) => theme.customShadows.card,
            minHeight: '500px',
            maxHeight: '600px',
            borderRadius: '20px',
            my: 'auto',
            bgcolor: 'background.paper',
            mx: 'auto',
          }}
        >
          <Logo disabledLink sx={{ mx: 'auto', display: 'block', '& img': { height: '90px', width: 'auto' } }} />
          <Typography variant="h5" sx={{ color: 'text.titles', minWidth: 'max-content', mt: '5.71px' }}>
            {isLogin ? 'Login to Your Account' : 'Forgot Your Password'}{' '}
          </Typography>
          <Box
            component="form"
            onSubmit={isLogin ? handleSubmit(onSubmit) : handleSubmit(forgotPasswordSubmit)}
            noValidate
            sx={{ mt: 1, width: '100%' }}
          >
            <TextField
              margin="normal"
              fullWidth
              id="email"
              label="Email"
              name="email"
              type="email"
              autoComplete="email"
              autoFocus
              {...register('email', {
                required: 'Email is Required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              error={errors && !!errors?.email}
              helperText={errors?.email?.message}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              {!isLogin && (
                <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                  <Box
                    component="a"
                    href=""
                    onClick={(e) => {
                      e.preventDefault();
                      setIsLogin(true);
                    }}
                    sx={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 400,
                      color: 'primary.main',
                      fontFamily: "'Inter', sans-serif",
                      paddingLeft: 0,
                      marginTop: '10px',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      '&:hover': { fontWeight: 'bold' },
                    }}
                  >
                    Back to Login
                  </Box>
                </Typography>
              )}

              {!isLogin && (
                <LoadingButton
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2, mb: 2, maxWidth: '118px' }}
                  color="main"
                  loading={isForgetPasswordsLoading}
                >
                  Submit
                </LoadingButton>
              )}
            </Box>

            {isLogin && (
              <>
                <Box
                  sx={{
                    position: 'relative',
                    // border: '5px solid red',
                    marginBottom: '0px',
                    marginTop: '0px',
                  }}
                >
                  <TextField
                    sx={{ marginBottom: '5px', marginTop: '5px' }}
                    margin="normal"
                    fullWidth
                    name="password"
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    autoComplete="current-password"
                    {...register('password', {
                      required: 'Password is Required',
                    })}
                    error={errors && !!errors?.password}
                    helperText={errors?.password?.message}
                  />

                  {showPassword && (
                    <Visibility
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        transform:
                          errors?.password || errors?.email ? 'translate(-50%, -85%)' : 'translate(-50%, -50%)',
                        right: '0px',
                        cursor: 'pointer',
                        color: 'text.secondary',
                      }}
                      onClick={() => setShowPassword(false)}
                    />
                  )}

                  {!showPassword && (
                    <VisibilityOff
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        transform:
                          errors?.password || errors?.email ? 'translate(-50%, -85%)' : 'translate(-50%, -50%)',
                        right: '0px',
                        cursor: 'pointer',
                        color: 'text.secondary',
                      }}
                      onClick={() => setShowPassword(true)}
                    />
                  )}
                </Box>

                <Box
                  component="a"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsLogin(!isLogin);
                  }}
                  sx={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: 'primary.main',
                    fontFamily: "'Inter', sans-serif",
                    paddingLeft: 0,
                    marginTop: '10px',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    '&:hover': { fontWeight: 'bold' },
                  }}
                >
                  Forgot Your Password?
                </Box>

                <LoadingButton
                  loading={loginLoading}
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2, maxWidth: '118px' }}
                  color="main"
                >
                  Login
                </LoadingButton>
                <Box
                  component="a"
                  href=""
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/onboard-user');
                  }}
                  sx={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 400,
                    color: 'primary.main',
                    paddingLeft: 0,
                    textDecoration: 'none',
                    fontFamily: "'Inter', sans-serif",
                    marginTop: '10px',
                    cursor: 'pointer',
                    '&:hover': { fontWeight: 'bold' },
                  }}
                >
                  Create Account / Onboard Yourself
                </Box>
              </>
            )}
          </Box>
          <Box sx={{ width: '100%', textAlign: 'left', marginTop: '1rem' }}>
            <Box
              component="a"
              href=""
              onClick={(e) => {
                e.preventDefault();
                navigate('/terms-and-conditions');
              }}
              sx={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 400,
                color: 'primary.main',
                textDecoration: 'none',
                fontFamily: "'Inter', sans-serif",
                marginTop: '10px',
                cursor: 'pointer',
                '&:hover': { fontWeight: 'bold' },
              }}
            >
              Terms and Conditions
            </Box>
          </Box>
        </Box>
      </Box>
      <ResendDialog open={showResendDialogue} handleClose={() => setShowResendDialogue(false)} email={loginCredentials?.email} />
    </Box>
  );
}
